import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Badge, 
    Modal, Alert, Spinner 
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { API_BASE_URL } from "../constants/api";


export default function Categories() {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedCategorie, setSelectedCategorie] = useState(null);
    const [saving, setSaving] = useState(false);
    
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [categorieToDelete, setCategorieToDelete] = useState(null);
    
    const [formData, setFormData] = useState({
        code_categorie: '',
        nom_categorie: '',
        description: ''
    });

    useEffect(() => {
        fetchCategories();
    }, []);

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 3000);
    };

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/categories.php`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setCategories(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.code_categorie || !formData.nom_categorie) {
            showError("Le code et le nom de la catégorie sont requis");
            return;
        }

        setSaving(true);

        try {
            const token = localStorage.getItem("token");
            let url = `${API_BASE_URL}/categories.php`;
            let method = 'POST';
            
            if (modalMode === 'edit') {
                url += `?id=${selectedCategorie.id_categorie}`;
                method = 'PUT';
            }
            
            const response = await fetch(url, {
                method: method,
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            const result = await response.json();

            if (result.success) {
                setShowModal(false);
                fetchCategories();
                showSuccess(modalMode === 'add' ? 'Catégorie créée avec succès' : 'Catégorie mise à jour avec succès');
            } else {
                showError(result.message || "Erreur lors de l'enregistrement");
            }
        } catch (err) {
            showError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteClick = (id, nom) => {
        setCategorieToDelete({ id, nom });
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        if (!categorieToDelete) return;
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/categories.php?id=${categorieToDelete.id}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            
            const result = await response.json();
            
            if (result.success) {
                setShowDeleteConfirm(false);
                setCategorieToDelete(null);
                fetchCategories();
                showSuccess("Catégorie supprimée avec succès");
            } else {
                showError(result.message || "Erreur lors de la suppression");
                setShowDeleteConfirm(false);
            }
        } catch (err) {
            showError("Erreur de connexion");
            setShowDeleteConfirm(false);
        }
    };

    const handleEdit = (categorie) => {
        setSelectedCategorie(categorie);
        setFormData({
            code_categorie: categorie.code_categorie,
            nom_categorie: categorie.nom_categorie,
            description: categorie.description || ''
        });
        setModalMode('edit');
        setShowModal(true);
    };

    const handleView = (categorie) => {
        setSelectedCategorie(categorie);
        setModalMode('view');
        setShowModal(true);
    };

    const handleAdd = () => {
        setFormData({
            code_categorie: '',
            nom_categorie: '',
            description: ''
        });
        setModalMode('add');
        setShowModal(true);
    };

    const filteredCategories = categories.filter(c => 
        c.code_categorie?.toLowerCase().includes(filterText.toLowerCase()) ||
        c.nom_categorie?.toLowerCase().includes(filterText.toLowerCase()) ||
        c.description?.toLowerCase().includes(filterText.toLowerCase())
    );

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchCategories}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* Alertes modernes */}
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {successMessage}
                        </Alert>
                    )}
                    
                    {showErrorAlert && (
                        <Alert variant="danger" onClose={() => setShowErrorAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {errorMessage}
                        </Alert>
                    )}

                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-price-tag-3-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Gestion des catégories</h4>
                                    <p className="text-muted small mb-0">Organisez vos produits par catégories</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="primary" 
                                onClick={handleAdd}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                                style={{ background: '#21ACCD', border: 'none' }}
                            >
                                <i className="ri-add-line"></i>
                                Nouvelle catégorie
                            </Button>
                        </div>
                    </div>

                    {/* Filtres modernes */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md={8}>
                                    <div className="position-relative">
                                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher catégorie..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="ps-5 rounded-pill"
                                        />
                                    </div>
                                </Col>
                                <Col md={4} className="text-end">
                                    <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                                        <i className="ri-stack-line me-1"></i>
                                        {filteredCategories.length} catégorie(s)
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Liste des catégories */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4">Code</th>
                                            <th>Nom</th>
                                            <th>Description</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="4" className="text-center py-5">
                                                    <Spinner animation="border" variant="primary" style={{ color: '#21ACCD' }} />
                                            </td>
                                            </tr>
                                        ) : filteredCategories.length > 0 ? (
                                            filteredCategories.map((categorie) => (
                                                <tr key={categorie.id_categorie} className="categorie-row">
                                                    <td className="ps-4">
                                                        <span className="reference-badge">
                                                            <i className="ri-barcode-line me-1 fs-10"></i>
                                                            {categorie.code_categorie}
                                                        </span>
                                                 </td>
                                                    <td className="fw-semibold">{categorie.nom_categorie}</td>
                                                    <td className="text-muted">{categorie.description || '-'}</td>
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <Button 
                                                                variant="outline-info" 
                                                                size="sm"
                                                                className="action-btn"
                                                                onClick={() => handleView(categorie)}
                                                            >
                                                                <i className="ri-eye-line"></i>
                                                            </Button>
                                                            <Button 
                                                                variant="outline-primary" 
                                                                size="sm"
                                                                className="action-btn"
                                                                onClick={() => handleEdit(categorie)}
                                                            >
                                                                <i className="ri-pencil-line"></i>
                                                            </Button>
                                                            <Button 
                                                                variant="outline-danger" 
                                                                size="sm"
                                                                className="action-btn"
                                                                onClick={() => handleDeleteClick(categorie.id_categorie, categorie.nom_categorie)}
                                                            >
                                                                <i className="ri-delete-bin-line"></i>
                                                            </Button>
                                                        </div>
                                         </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <i className="ri-price-tag-3-line empty-icon text-primary" style={{ fontSize: '4rem' }}></i>
                                                        <h5 className="mt-3">Aucune catégorie trouvée</h5>
                                                        <p className="text-muted mb-3">
                                                            {filterText 
                                                                ? "Aucun résultat ne correspond à votre recherche"
                                                                : "Commencez par créer votre première catégorie"}
                                                        </p>
                                                        {filterText ? (
                                                            <Button 
                                                                variant="outline-primary"
                                                                onClick={() => setFilterText('')}
                                                                className="rounded-pill"
                                                            >
                                                                <i className="ri-refresh-line me-2"></i>Réinitialiser
                                                            </Button>
                                                        ) : (
                                                            <Button 
                                                                variant="primary" 
                                                                onClick={handleAdd}
                                                                className="rounded-pill"
                                                                style={{ background: '#21ACCD', border: 'none' }}
                                                            >
                                                                <i className="ri-add-line me-2"></i>Créer une catégorie
                                                            </Button>
                                                        )}
                                                    </div>
                                              </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* Modal Catégorie modernisé */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0" style={{ background: modalMode === 'view' ? '#21ACCD' : '#21ACCD' }}>
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="confirm-icon mb-2">
                                <i className="ri-price-tag-3-line text-white" style={{ fontSize: '3rem' }}></i>
                            </div>
                            <h5 className="fw-bold text-white">
                                {modalMode === 'add' && 'Nouvelle catégorie'}
                                {modalMode === 'edit' && 'Modifier la catégorie'}
                                {modalMode === 'view' && 'Détails de la catégorie'}
                            </h5>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body className="pt-4">
                        {modalMode === 'view' && selectedCategorie ? (
                            <div className="text-center py-3">
                                <div className="view-icon mx-auto mb-3">
                                    <i className="ri-price-tag-3-line text-primary" style={{ fontSize: '3rem' }}></i>
                                </div>
                                <h4 className="fw-bold">{selectedCategorie.nom_categorie}</h4>
                                <div className="mt-3">
                                    <Badge className="px-3 py-2 rounded-pill" style={{ background: '#21ACCD' }}>
                                        Code: {selectedCategorie.code_categorie}
                                    </Badge>
                                </div>
                                {selectedCategorie.description && (
                                    <div className="mt-3 p-3 bg-light rounded-3">
                                        <small className="text-muted d-block mb-1">Description</small>
                                        <p className="mb-0">{selectedCategorie.description}</p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold">Code <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={formData.code_categorie}
                                            onChange={(e) => setFormData({...formData, code_categorie: e.target.value.toUpperCase()})}
                                            disabled={modalMode === 'edit'}
                                            required
                                            className="border-2 rounded-3"
                                            placeholder="Ex: ELEC"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold">Nom <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={formData.nom_categorie}
                                            onChange={(e) => setFormData({...formData, nom_categorie: e.target.value})}
                                            required
                                            className="border-2 rounded-3"
                                            placeholder="Ex: Électronique"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold">Description</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={formData.description}
                                            onChange={(e) => setFormData({...formData, description: e.target.value})}
                                            className="border-2 rounded-3"
                                            placeholder="Description de la catégorie..."
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="border-0 pb-4 justify-content-center">
                        <Button variant="outline-secondary" onClick={() => setShowModal(false)} className="rounded-pill px-4">
                            Fermer
                        </Button>
                        {modalMode !== 'view' && (
                            <Button 
                                variant="primary" 
                                type="submit" 
                                disabled={saving}
                                className="rounded-pill px-5"
                                style={{ background: '#21ACCD', border: 'none' }}
                            >
                                {saving ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Enregistrement...
                                    </>
                                ) : (
                                    modalMode === 'add' ? 'Créer' : 'Mettre à jour'
                                )}
                            </Button>
                        )}
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal de confirmation de suppression modernisé */}
            <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#dc2626' }}>
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="confirm-icon mb-2">
                                <i className="ri-delete-bin-line text-white" style={{ fontSize: '3rem' }}></i>
                            </div>
                            <h5 className="fw-bold text-white">Confirmer la suppression</h5>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-4 text-center">
                    <p className="text-muted mb-3">
                        Êtes-vous sûr de vouloir supprimer la catégorie ?
                    </p>
                    <div className="bg-light p-3 rounded-3 mb-3">
                        <strong className="text-primary">{categorieToDelete?.nom}</strong>
                        <div><small className="text-muted">Code: {categorieToDelete?.code}</small></div>
                    </div>
                    <Alert variant="danger" className="rounded-3">
                        <i className="ri-alert-line me-2"></i>
                        Cette action est irréversible.
                    </Alert>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                    <Button variant="outline-secondary" onClick={() => setShowDeleteConfirm(false)} className="rounded-pill px-4">
                        Annuler
                    </Button>
                    <Button variant="danger" onClick={confirmDelete} className="rounded-pill px-5">
                        Confirmer
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                /* Reference Badge */
                .reference-badge {
                    background: #f1f5f9;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-family: monospace;
                    display: inline-block;
                }
                
                /* Action Buttons */
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }
                .btn-outline-primary:hover {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                }
                .btn-outline-danger:hover {
                    background: linear-gradient(135deg, #ef4444, #dc2626);
                    border-color: #ef4444;
                    color: white;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .categorie-row {
                    transition: all 0.2s ease;
                }
                
                .categorie-row:hover {
                    background: #f8fafc;
                }
                
                /* View Icon */
                .view-icon {
                    width: 80px;
                    height: 80px;
                    background: #e2f3f5;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto;
                }
                
                /* Modern Modal */
                .modern-modal .modal-content {
                    border-radius: 20px;
                    border: none;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                    overflow: hidden;
                }
                
                .confirm-icon {
                    animation: scaleIn 0.5s ease-out;
                }
                
                @keyframes scaleIn {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); opacity: 1; }
                }
                
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .border-2 {
                    border-width: 2px !important;
                    border-color: #e2e8f0;
                }
                .border-2:focus {
                    border-color: #21ACCD;
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
            `}</style>
        </>
    );
}